import { useCallback, useState, useContext, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ChatContextType } from '@trinity/chat';
import { ChatCustomFilter } from '@trinity/chat';
import type { CustomFilter } from '@trinity/chat';
import * as $cf from './custom-filters';
import { CustomFilterDateFilter } from './CustomFilter.DateSelect';
import { CustomFilterMultiselect } from './CustomFilter.Multiselect';
import { useFilterSearchQuery } from './hooks/useFilterSearch';

type Props<T extends ChatCustomFilter = ChatCustomFilter> = {
  onSelect: (value: CustomFilter.Item['value']) => void;
  value: CustomFilter.Item<T>;
};

export function CustomFilterValue(props: Props) {

  switch (props.value.type) {

    case ChatCustomFilter.PracticeSetting:
      return (
        <CustomFilterMultiselect
          placeholder="Select Practice Setting"
          onSelect={props.onSelect}
          options={$cf.getPracticeSettings()}
          value={props.value.value} />
      );

    case ChatCustomFilter.InsightType:
      return (
        <InsightTypeSelect
          onSelect={props.onSelect}
          value={props.value} />
      );

    case ChatCustomFilter.Product:
      return (
        <ProductFilterSelect
          onSelect={props.onSelect}
          value={props.value} />
      );

    case ChatCustomFilter.Source:
      return null;

    case ChatCustomFilter.TherapeuticArea:
      return (
        <TherapeuticAreasSelect
          onSelect={props.onSelect}
          value={props.value} />
      );

    case ChatCustomFilter.TherapeuticSubArea:
      return (
        <TherapeuticSubAreaFilterSelect
          onSelect={props.onSelect}
          value={props.value} />
      );

    case ChatCustomFilter.Sentiment:
      return (
        <CustomFilterMultiselect
          placeholder="Select Sentiment"
          isSearchable={false}
          onSelect={props.onSelect}
          options={$cf.getSentimentValues()}
          value={props.value.value} />
      );

    case ChatCustomFilter.InteractionDate:
      return (
        <CustomFilterDateFilter
          onSelect={props.onSelect}
          value={props.value.value} />
      );

    case ChatCustomFilter.InterestArea:
      return (
        <InterestAreaFilterSelect
          onSelect={props.onSelect}
          value={props.value} />
      );

    case ChatCustomFilter.InteractionType:
      return (
        <CustomFilterMultiselect
          placeholder="Select Interaction Type"
          onSelect={props.onSelect}
          options={$cf.getInteractionTypes()}
          value={props.value.value} />
      );

    case ChatCustomFilter.InteractionSubType:
      return (
        <InteractionSubTypeSelect
          onSelect={props.onSelect}
          value={props.value} />
      );

    case ChatCustomFilter.InteractionDetail:
      return (
        <InteractionDetailSelect
          onSelect={props.onSelect}
          value={props.value} />
      );

    case ChatCustomFilter.CallCenterSource:
      return (
        <CallCenterSourceSelect
          onSelect={props.onSelect}
          value={props.value} />
      );

    case ChatCustomFilter.CallCenterSubSource:
      return (
        <CallCenterSubSourceSelect
          onSelect={props.onSelect}
          value={props.value} />
      );
    case ChatCustomFilter.AgentFilter:
    case ChatCustomFilter.InteractionStartEndDate:
      return null;
    default:
      throw new UnreachableCaseError(props.value);
  }
}

type TSAFilterSelectProps = {
  onSelect: (value: CustomFilter.TherapeuticSubArea['value']) => void;
  value: CustomFilter.TherapeuticSubArea;
};

const TherapeuticSubAreaFilterSelect = (props: TSAFilterSelectProps) => {

  const [input, setInput] = useState('');
  const brandIdentifier = useActiveBrandIdentifier();
  const handleInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  const query = useFilterSearchQuery({
    excluded: props.value.value.map(m => m.id),
    type: ChatCustomFilter.TherapeuticSubArea,
    value: input,
    brandIdentifier,
  });

  const loadingMessage = useCallback(() => {
    return '...Searching';
  }, []);

  return (
    <CustomFilterMultiselect
      components={{
        DropdownIndicator: null,
      }}
      placeholder="Select Therapeutic Sub Areas"
      inputValue={input}
      onInputChange={handleInputChange}
      loadingMessage={loadingMessage}
      isLoading={query.isLoading || query.isFetching}
      isSearchable={true}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

type ProductFilterSelectProps = {
  onSelect: (value: CustomFilter.Product['value']) => void;
  value: CustomFilter.Product;
};

const ProductFilterSelect = (props: ProductFilterSelectProps) => {

  const [input, setInput] = useState('');
  const brandIdentifier = useActiveBrandIdentifier();
  const handleInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  const query = useFilterSearchQuery({
    excluded: props.value.value.map(m => m.id),
    type: ChatCustomFilter.Product,
    value: input,
    brandIdentifier,
  });

  const loadingMessage = useCallback(() => {
    return '...Searching';
  }, []);

  return (
    <CustomFilterMultiselect
      components={{
        DropdownIndicator: null,
      }}
      placeholder="Select Products"
      inputValue={input}
      onInputChange={handleInputChange}
      loadingMessage={loadingMessage}
      isLoading={query.isLoading || query.isFetching}
      isSearchable={true}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

type IAFilterSelectProps = {
  onSelect: (value: CustomFilter.InterestArea['value']) => void;
  value: CustomFilter.InterestArea;
};

const InterestAreaFilterSelect = (props: IAFilterSelectProps) => {

  const [input, setInput] = useState('');
  const brandIdentifier = useActiveBrandIdentifier();
  const handleInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  const query = useFilterSearchQuery({
    excluded: props.value.value.map(m => m.id),
    type: ChatCustomFilter.InterestArea,
    value: input,
    brandIdentifier,
  });

  const loadingMessage = useCallback(() => {
    return '...Searching';
  }, []);

  return (
    <CustomFilterMultiselect
      components={{
        DropdownIndicator: null,
      }}
      placeholder="Select Interest Areas"
      inputValue={input}
      onInputChange={handleInputChange}
      loadingMessage={loadingMessage}
      isLoading={query.isLoading || query.isFetching}
      isSearchable={true}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

type TherapeuticAreasSelectProps = {
  onSelect: (value: CustomFilter.InterestArea['value']) => void;
  value: CustomFilter.TherapeuticArea;
};

const TherapeuticAreasSelect = (props: TherapeuticAreasSelectProps) => {

  const [input, setInput] = useState('');
  const brandIdentifier = useActiveBrandIdentifier();
  const handleInputChange = useCallback((value: string) => {
    setInput(value);
  }, []);

  const query = useFilterSearchQuery({
    excluded: props.value.value.map(m => m.id),
    type: ChatCustomFilter.TherapeuticArea,
    value: input,
    brandIdentifier,
  });

  const loadingMessage = useCallback(() => {
    return '...Searching';
  }, []);

  return (
    <CustomFilterMultiselect
      components={{
        DropdownIndicator: null,
      }}
      placeholder="Select Therapeutic Areas"
      inputValue={input}
      onInputChange={handleInputChange}
      loadingMessage={loadingMessage}
      isLoading={query.isLoading || query.isFetching}
      isSearchable={true}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

type InteractionSubTypeProps = {
  onSelect: (value: CustomFilter.InteractionSubType['value']) => void;
  value: CustomFilter.InteractionSubType;
};

const InteractionSubTypeSelect = (props: InteractionSubTypeProps) => {

  const brandIdentifier = useActiveBrandIdentifier();

  const query = useFilterSearchQuery({
    excluded: [], // props.value.value.map(m => m.id),
    type: ChatCustomFilter.InteractionSubType,
    value: '',
    brandIdentifier,
  });

  return (
    <CustomFilterMultiselect
      placeholder="Select Interaction Sub Type"
      isLoading={query.isLoading || query.isFetching}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

const CallCenterSourceSelect = (props: Props<ChatCustomFilter.CallCenterSource>) => {
  const brandIdentifier = useActiveBrandIdentifier();

  const query = useFilterSearchQuery({
    excluded: [], // props.value.value.map(m => m.id),
    type: ChatCustomFilter.CallCenterSource,
    value: '',
    brandIdentifier,
  });

  return (
    <CustomFilterMultiselect
      placeholder="Select Call Center Source"
      isLoading={query.isLoading || query.isFetching}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

const CallCenterSubSourceSelect = (props: Props<ChatCustomFilter.CallCenterSubSource>) => {
  const brandIdentifier = useActiveBrandIdentifier();

  const query = useFilterSearchQuery({
    excluded: [], // props.value.value.map(m => m.id),
    type: ChatCustomFilter.CallCenterSubSource,
    value: '',
    brandIdentifier,
  });

  return (
    <CustomFilterMultiselect
      placeholder="Select Call Center Sub Source"
      isLoading={query.isLoading || query.isFetching}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

const InteractionDetailSelect = (props: Props<ChatCustomFilter.InteractionDetail>) => {
  const brandIdentifier = useActiveBrandIdentifier();

  const query = useFilterSearchQuery({
    excluded: [], // props.value.value.map(m => m.id),
    type: ChatCustomFilter.InteractionDetail,
    value: '',
    brandIdentifier,
  });

  return (
    <CustomFilterMultiselect
      placeholder="Select Interaction Detail"
      isLoading={query.isLoading || query.isFetching}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};

const InsightTypeSelect = (props: Props<ChatCustomFilter.InsightType>) => {
  const brandIdentifier = useActiveBrandIdentifier();

  const query = useFilterSearchQuery({
    excluded: [], // props.value.value.map(m => m.id),
    type: ChatCustomFilter.InsightType,
    value: '',
    brandIdentifier,
  });

  return (
    <CustomFilterMultiselect
      placeholder="Select Insight Type"
      isLoading={query.isLoading || query.isFetching}
      onSelect={props.onSelect}
      options={query.data.results}
      value={props.value.value} />
  );
};